import { useEffect, useState } from 'react';
import { App, Button, Tag } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import { useLinkAccount, usePrivy } from '@privy-io/react-auth';
import { getApiErrorMessage, useAuth } from '../../../auth/AuthContext';
import { markPasskeySetup } from '../../../auth/storage';
import { useTranslation } from 'react-i18next';

function PasskeyCard() {
    const { t } = useTranslation('account');
    const { message } = App.useApp();
    const { loginWithPrivy } = useAuth();
    const { ready, authenticated, user } = usePrivy();
    const [isLinking, setIsLinking] = useState(false);

    const { linkPasskey } = useLinkAccount({
        onSuccess: async ({ linkMethod }) => {
            if (linkMethod !== 'passkey') return;

            markPasskeySetup();
            try {
                await loginWithPrivy();
                message.success(t('passkey.linked'));
            } catch (error) {
                message.error(getApiErrorMessage(error, t('passkey.syncFailed')));
            } finally {
                setIsLinking(false);
            }
        },
        onError: (error, { linkMethod }) => {
            if (linkMethod !== 'passkey') return;
            setIsLinking(false);
            message.error(t('passkey.linkFailed', { error: String(error) }));
        },
    });
    const hasPasskey = Boolean(
        user?.linkedAccounts.some((account) => account.type === 'passkey')
    );

    useEffect(() => {
        if (hasPasskey) {
            markPasskeySetup();
        }
    }, [hasPasskey]);

    const handleSetup = () => {
        if (!authenticated) {
            message.error(t('passkey.sessionRequired'));
            return;
        }

        setIsLinking(true);
        linkPasskey({ name: 'APC Plus passkey' });
    };

    return (
        <section className="panel account-passkey" aria-labelledby="passkey-title">
            <div className="account-passkey__icon" aria-hidden="true">
                <SafetyCertificateOutlined />
            </div>

            <div className="account-passkey__content">
                <div className="account-passkey__title-row">
                    <h2 id="passkey-title">{t('passkey.title')}</h2>
                    <Tag
                        className={`account-passkey__status${
                            hasPasskey ? ' account-passkey__status--active' : ''
                        }`}
                    >
                        {hasPasskey ? t('passkey.active') : t('passkey.notSetUp')}
                    </Tag>
                </div>

                <p>
                    {t('passkey.description')}
                </p>
            </div>

            <Button
                type="primary"
                htmlType="button"
                className="account-passkey__action"
                loading={isLinking}
                disabled={!ready || !authenticated}
                onClick={handleSetup}
            >
                {hasPasskey ? t('passkey.addAnother') : t('passkey.setup')}
            </Button>
        </section>
    );
}

export default PasskeyCard;
